// FieldIQ — User survey: Today-screen alert + full-screen answer sheet.
//
// A "user" survey (surveys.audience = 'user') is authored in the Client Portal
// and targets every logged-in user of the facility. There is no token or link:
// the survey is resolved from the caller's JWT. It stays editable for as long
// as it is published — closing it in the portal locks the answers and drops
// the alert.

import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronLeft,
  Loader2,
} from "lucide-react";
import { authFetch } from "../lib/api";

const isBlank = (v) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length);

// ── Question renderer ──────────────────────────────────────────────
const Question = ({ q, value, onChange }) => {
  const opts = q.options || [];

  const pill = (active) =>
    `px-3.5 py-2 rounded-full text-[13px] font-semibold border transition-colors ${
      active
        ? "bg-cyan-500/15 border-cyan-500/60 text-cyan-300"
        : "bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600"
    }`;

  const input =
    "w-full bg-slate-800 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/60";

  return (
    <div className="space-y-2.5">
      <p className="text-[14px] font-semibold text-white leading-snug">
        {q.label}
        {q.required && <span className="text-red-400 ml-1">*</span>}
      </p>

      {q.type === "long_text" && (
        <textarea
          rows={4}
          className={`${input} resize-none`}
          placeholder="Your answer…"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {q.type === "short_text" && (
        <input
          className={input}
          placeholder="Your answer…"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {q.type === "number" && (
        <input
          type="number"
          inputMode="numeric"
          className={input}
          placeholder="0"
          value={value ?? ""}
          onChange={(e) =>
            onChange(e.target.value === "" ? "" : Number(e.target.value))
          }
        />
      )}

      {q.type === "boolean" && (
        <div className="flex gap-2">
          {[
            { v: true, label: "Yes" },
            { v: false, label: "No" },
          ].map(({ v, label }) => (
            <button
              key={label}
              type="button"
              onClick={() => onChange(value === v ? null : v)}
              className={pill(value === v)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {q.type === "scale" && (
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(value === n ? null : n)}
              className={`flex-1 py-2.5 rounded-xl text-[15px] font-bold border transition-colors ${
                value === n
                  ? "bg-cyan-500/15 border-cyan-500/60 text-cyan-300"
                  : "bg-slate-800 border-slate-700 text-slate-400"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      )}

      {q.type === "single_select" && (
        <div className="flex flex-wrap gap-2">
          {opts.filter(Boolean).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(value === opt ? null : opt)}
              className={pill(value === opt)}
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {q.type === "multi_select" && (
        <div className="flex flex-wrap gap-2">
          {opts.filter(Boolean).map((opt) => {
            const list = Array.isArray(value) ? value : [];
            const on = list.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() =>
                  onChange(on ? list.filter((x) => x !== opt) : [...list, opt])
                }
                className={`${pill(on)} inline-flex items-center gap-1.5`}
              >
                {on && <Check size={13} />}
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Full-screen answer sheet ───────────────────────────────────────
const UserSurveySheet = ({
  survey,
  facility,
  initialAnswers,
  onClose,
  onSaved,
}) => {
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const schema = Array.isArray(survey.schema) ? survey.schema : [];
  const missing = schema
    .filter((q) => q.required && isBlank(answers[q.id]))
    .map((q) => q.label || q.id);

  const save = async () => {
    if (missing.length) {
      setErr(`Please answer: ${missing.join(", ")}`);
      return;
    }
    setSaving(true);
    setErr("");
    const res = await authFetch(`/fieldiq/user-survey/${survey.id}/response`, {
      method: "POST",
      body: JSON.stringify({
        facility_id: facility?.facility_id,
        answers,
      }),
    });
    setSaving(false);
    if (res?.error) {
      setErr(res.error);
      return;
    }
    onSaved(answers);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 overflow-y-auto">
      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 text-slate-400 text-sm hover:text-slate-200"
        >
          <ChevronLeft size={16} /> Back
        </button>

        <div>
          <h1 className="text-lg font-extrabold text-white leading-snug">
            {survey.title || "Survey"}
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            {schema.length} question{schema.length !== 1 ? "s" : ""} · you can
            change your answers later while this survey is open
          </p>
        </div>

        <div className="space-y-6">
          {schema.map((q) => (
            <Question
              key={q.id}
              q={q}
              value={answers[q.id]}
              onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
            />
          ))}
        </div>

        {err && (
          <div className="px-3.5 py-2.5 rounded-xl bg-red-900/40 border border-red-700/50 text-red-300 text-xs">
            {err}
          </div>
        )}

        <div className="flex gap-2 pb-8">
          <button
            onClick={onClose}
            className="px-4 py-3 rounded-xl border border-slate-700 text-slate-300 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-bold transition-colors disabled:opacity-50"
          >
            {saving && <Loader2 size={15} className="animate-spin" />}
            Submit answers
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Today-screen alert ─────────────────────────────────────────────
export const UserSurveyAlert = ({ facility, isOnline = true }) => {
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState(null); // null = not answered yet
  const [open, setOpen] = useState(false);

  const fid = facility?.facility_id;

  const load = useCallback(() => {
    if (!fid || !navigator.onLine) return;
    authFetch(`/fieldiq/user-survey?facility_id=${encodeURIComponent(fid)}`)
      .then((data) => {
        if (data?.error) return;
        setSurvey(data?.survey || null);
        setAnswers(data?.response?.answers ?? null);
      })
      .catch(() => {});
  }, [fid]);

  useEffect(() => {
    load();
  }, [load]);

  // A survey closed while the app was open should stop nagging; refresh when
  // the PWA comes back to the foreground.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  if (!survey || !isOnline) return null;

  const answered = answers !== null;
  const qCount = Array.isArray(survey.schema) ? survey.schema.length : 0;

  return (
    <>
      {open && (
        <UserSurveySheet
          survey={survey}
          facility={facility}
          initialAnswers={answers}
          onClose={() => setOpen(false)}
          onSaved={(saved) => {
            setAnswers(saved);
            setOpen(false);
          }}
        />
      )}

      <button
        onClick={() => setOpen(true)}
        className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl border text-left transition-colors ${
          answered
            ? "bg-emerald-950/40 border-emerald-700/50 hover:border-emerald-600"
            : "bg-red-950/50 border-red-600/60 hover:border-red-500"
        }`}
      >
        <div
          className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg ${
            answered
              ? "bg-emerald-500/15 border border-emerald-500/25"
              : "bg-red-500/15 border border-red-500/30"
          }`}
        >
          {answered ? (
            <CheckCircle2 size={16} className="text-emerald-400" />
          ) : (
            <AlertTriangle size={16} className="text-red-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={`text-[13.5px] font-extrabold truncate ${
              answered ? "text-emerald-300" : "text-red-300"
            }`}
          >
            {survey.title || "Survey"}
          </p>
          <p className="text-[11px] font-medium text-slate-400 mt-px truncate">
            {answered
              ? "Answers submitted — tap to edit"
              : `${qCount} question${qCount !== 1 ? "s" : ""} · action needed`}
          </p>
        </div>
        <span
          className={`flex-shrink-0 text-[11px] font-bold uppercase tracking-wide ${
            answered ? "text-emerald-400" : "text-red-400"
          }`}
        >
          {answered ? "Edit" : "Start"}
        </span>
      </button>
    </>
  );
};
